import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Trophy, Plus, Pencil, Trash, Eye, Calendar, Clock,
  Users, FileText, Check, X, MagnifyingGlass
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CmsCompetitions() {
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCompetition, setEditingCompetition] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    theme: '',
    rules: '',
    prizes: '',
    start_date: '',
    end_date: '',
    submission_deadline: '',
    voting_deadline: '',
    max_entries_per_user: 1,
    entry_word_limit: 500,
    status: 'upcoming',
    entry_types: ['poem', 'story', 'essay']
  });

  useEffect(() => {
    fetchCompetitions();
  }, []);

  const fetchCompetitions = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/admin/competitions`, {
        withCredentials: true
      });
      setCompetitions(res.data.competitions || res.data || []);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load competitions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCompetition) {
        await axios.put(
          `${API}/api/admin/competitions/${editingCompetition.competition_id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Competition updated successfully');
      } else {
        await axios.post(
          `${API}/api/admin/competitions`,
          formData,
          { withCredentials: true }
        );
        toast.success('Competition created successfully');
      }
      
      setShowModal(false);
      resetForm();
      fetchCompetitions();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Failed to save competition');
    }
  };

  const handleDelete = async (competitionId) => {
    if (!window.confirm('Are you sure you want to delete this competition? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/api/admin/competitions/${competitionId}`, {
        withCredentials: true
      });
      toast.success('Competition deleted');
      fetchCompetitions();
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete competition');
    }
  };

  const handleEdit = (competition) => {
    setEditingCompetition(competition);
    setFormData({
      title: competition.title || '',
      description: competition.description || '',
      theme: competition.theme || '',
      rules: competition.rules || '',
      prizes: competition.prizes || '',
      start_date: competition.start_date?.split('T')[0] || '',
      end_date: competition.end_date?.split('T')[0] || '',
      submission_deadline: competition.submission_deadline?.split('T')[0] || '',
      voting_deadline: competition.voting_deadline?.split('T')[0] || '',
      max_entries_per_user: competition.max_entries_per_user || 1,
      entry_word_limit: competition.entry_word_limit || 500,
      status: competition.status || 'upcoming',
      entry_types: competition.entry_types || ['poem', 'story', 'essay']
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingCompetition(null);
    setFormData({
      title: '',
      description: '',
      theme: '',
      rules: '',
      prizes: '',
      start_date: '',
      end_date: '',
      submission_deadline: '',
      voting_deadline: '',
      max_entries_per_user: 1,
      entry_word_limit: 500,
      status: 'upcoming',
      entry_types: ['poem', 'story', 'essay']
    });
  };

  const statusColors = {
    upcoming: 'bg-amber-100 text-amber-700 border-amber-200',
    active: 'bg-green-100 text-green-700 border-green-200',
    voting: 'bg-blue-100 text-blue-700 border-blue-200',
    closed: 'bg-gray-100 text-gray-700 border-gray-200'
  };

  const filteredCompetitions = competitions.filter(comp => {
    const matchesSearch = comp.title?.toLowerCase().includes(search.toLowerCase()) ||
                         comp.theme?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || comp.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Trophy size={32} weight="fill" className="text-primary" />
            Competition Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Create and manage writing competitions
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Plus size={20} weight="bold" />
          New Competition
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search competitions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 bg-card border border-border rounded-lg"
        >
          <option value="all">All Status</option>
          <option value="upcoming">Upcoming</option>
          <option value="active">Active</option>
          <option value="voting">Voting</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      {/* Competitions List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : filteredCompetitions.length === 0 ? (
        <div className="text-center py-12 bg-card border border-border rounded-lg">
          <Trophy size={48} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">
            {search || statusFilter !== 'all' ? 'No competitions match your filters' : 'No competitions yet. Create your first one!'}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredCompetitions.map((competition) => (
            <div
              key={competition.competition_id}
              className="bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-colors"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold">{competition.title}</h3>
                    <span className={`px-2 py-1 text-xs rounded border ${statusColors[competition.status]}`}>
                      {competition.status}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{competition.description}</p>
                  {competition.theme && (
                    <div className="text-sm">
                      <strong>Theme:</strong> {competition.theme}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(competition)}
                    className="p-2 hover:bg-muted rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Pencil size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(competition.competition_id)}
                    className="p-2 hover:bg-destructive/10 text-destructive rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash size={18} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-muted-foreground" />
                  <div>
                    <div className="text-xs text-muted-foreground">Starts</div>
                    <div>{new Date(competition.start_date).toLocaleDateString()}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-muted-foreground" />
                  <div>
                    <div className="text-xs text-muted-foreground">Ends</div>
                    <div>{new Date(competition.end_date).toLocaleDateString()}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Users size={16} className="text-muted-foreground" />
                  <div>
                    <div className="text-xs text-muted-foreground">Entries</div>
                    <Link
                      to={`/admin/competitions/${competition.competition_id}/entries`}
                      className="text-primary hover:underline"
                    >
                      {competition.entries_count || 0} entries
                    </Link>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <FileText size={16} className="text-muted-foreground" />
                  <div>
                    <div className="text-xs text-muted-foreground">Word Limit</div>
                    <div>{competition.entry_word_limit}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold">
                {editingCompetition ? 'Edit Competition' : 'Create New Competition'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="p-2 hover:bg-muted rounded-lg"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Competition Title *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                  placeholder="e.g., Monthly Poetry Contest - Monsoon Edition"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Description *
                </label>
                <textarea
                  required
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                  placeholder="Brief description of the competition..."
                />
              </div>

              {/* Theme */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Theme (Optional)
                </label>
                <input
                  type="text"
                  value={formData.theme}
                  onChange={(e) => setFormData({...formData, theme: e.target.value})}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                  placeholder="e.g., Monsoon, Love, Nature"
                />
              </div>

              {/* Rules */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Rules & Guidelines
                </label>
                <textarea
                  rows={4}
                  value={formData.rules}
                  onChange={(e) => setFormData({...formData, rules: e.target.value})}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                  placeholder="Competition rules, eligibility criteria, etc."
                />
              </div>

              {/* Prizes */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Prizes
                </label>
                <textarea
                  rows={2}
                  value={formData.prizes}
                  onChange={(e) => setFormData({...formData, prizes: e.target.value})}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                  placeholder="1st Prize: ₹5000, 2nd Prize: ₹3000, 3rd Prize: ₹2000"
                />
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Start Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">End Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Submission Deadline *</label>
                  <input
                    type="date"
                    required
                    value={formData.submission_deadline}
                    onChange={(e) => setFormData({...formData, submission_deadline: e.target.value})}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Voting Deadline</label>
                  <input
                    type="date"
                    value={formData.voting_deadline}
                    onChange={(e) => setFormData({...formData, voting_deadline: e.target.value})}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                  />
                </div>
              </div>

              {/* Limits */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Max Entries Per User</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.max_entries_per_user}
                    onChange={(e) => setFormData({...formData, max_entries_per_user: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Word Limit</label>
                  <input
                    type="number"
                    min="50"
                    value={formData.entry_word_limit}
                    onChange={(e) => setFormData({...formData, entry_word_limit: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                  />
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                >
                  <option value="upcoming">Upcoming</option>
                  <option value="active">Active</option>
                  <option value="voting">Voting</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              {/* Entry Types */}
              <div>
                <label className="block text-sm font-medium mb-2">Allowed Entry Types</label>
                <div className="flex gap-4">
                  {['poem', 'story', 'essay', 'article'].map(type => (
                    <label key={type} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.entry_types.includes(type)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({...formData, entry_types: [...formData.entry_types, type]});
                          } else {
                            setFormData({...formData, entry_types: formData.entry_types.filter(t => t !== type)});
                          }
                        }}
                        className="w-4 h-4"
                      />
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </label>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  {editingCompetition ? 'Update Competition' : 'Create Competition'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
